<template>
    <v-row v-if="!loading && ticketLoaded" class="ma-0 pb-offset">
        <!-- Colonna sinistra: Email Thread e Reply (8/12) -->
        <v-col cols="12" lg="8" class="pa-0">
            <!-- Header Card con Ticket Info e Stepper -->
            <v-card class="round-card background-2 mb-4">
                <v-card-title class="pa-3">
                    <v-icon color="primary" class="mr-3">mdi-ticket</v-icon>
                    <p>Ticket #{{ ticket.number || ticket.id }}</p>
                    <p class="entity-title">&nbsp;&nbsp;{{ ticket.title }}</p>

                    <v-spacer></v-spacer>
                    <!-- Campi modificabili al volo: click sulla chip → menu inline.
                         L'editabilità rispetta i permessi (come in edit); il backend
                         applica comunque lo stesso permesso dell'edit. -->
                    <!-- Sempre visibile: mostra gli assegnatari (o "Nessun assegnatario")
                         anche in sola lettura, così lo stato di assegnazione è esplicito. -->
                    <ticket-inline-edit
                        chip-class="mr-2"
                        field="users"
                        multiple
                        icon="mdi-account-multiple"
                        outlined
                        field-label="Utenti assegnati"
                        empty-text="Nessun assegnatario"
                        :ticket-id="id"
                        :editable="canEditTicket"
                        :value="assignedUserIds"
                        :options="userOptions"
                        @saved="onFieldSaved"
                    ></ticket-inline-edit>
                    <ticket-inline-edit
                        chip-class="mr-2"
                        field="status"
                        dark
                        field-label="Stato"
                        :ticket-id="id"
                        :editable="canEditTicket"
                        :value="ticket.status"
                        :options="statusOptions"
                        :color="ticket.trouble_status_color"
                        @saved="onFieldSaved"
                    ></ticket-inline-edit>
                    <ticket-inline-edit
                        v-if="canEditTicket || ticket.request_type_text"
                        chip-class="mr-2"
                        field="request_type"
                        outlined
                        square
                        field-label="Tipo di richiesta"
                        empty-text="Tipo"
                        :ticket-id="id"
                        :editable="canEditTicket"
                        :value="ticket.request_type"
                        :options="requestTypeOptions"
                        :color="ticket.request_type_color"
                        @saved="onFieldSaved"
                    ></ticket-inline-edit>
                    <ticket-inline-edit
                        chip-class="px-3"
                        field="priority"
                        outlined
                        field-label="Priorità"
                        :ticket-id="id"
                        :editable="canEditTicket"
                        :value="ticket.priority"
                        :options="priorityOptions"
                        :color="ticket.trouble_priority_color"
                        @saved="onFieldSaved"
                    ></ticket-inline-edit>
                    <button-show-generator
                        v-if="ticketInfo && ticketInfo.buttonShowDefsNew"
                        :id="ticket.id"
                        :info="ticketInfo"
                        :model="ticket"
                        class="ml-2"
                    ></button-show-generator>
                    <!-- Freccette prev/next tra ticket fratelli, come nelle Show standard -->
                    <entity-navigation
                        model-def="helpdesktroubles"
                        :current-id="id"
                    ></entity-navigation>
                </v-card-title>

                <v-divider></v-divider>

                <!-- Stepper per condition -->
                <v-stepper v-model="currentStep" flat alt-labels class="elevation-0">
                    <v-stepper-header>
                        <v-stepper-step :complete="currentStep > 1" step="1" class="fs13">
                            Triage
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step :complete="currentStep > 2" step="2" class="fs13">
                            Service Desk
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step :complete="currentStep >= 3" step="3" class="fs13">
                            Risolto
                        </v-stepper-step>
                    </v-stepper-header>
                </v-stepper>

                <v-divider></v-divider>
                
                <v-card-actions>
                    <v-btn
                        color="primary"
                        text
                        @click="togglePanel('triage')"
                    >
                        <v-icon left>{{ showTriagePanel ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
                        {{ showTriagePanel ? 'Annulla' : 'Triage' }}
                    </v-btn>
                    <v-tooltip bottom :disabled="!interventionBlocked">
                        <template v-slot:activator="{ on, attrs }">
                            <div v-on="on" v-bind="attrs" class="d-inline-block">
                                <v-btn
                                    color="success"
                                    text
                                    :disabled="interventionBlocked && !showInterventionPanel"
                                    @click="togglePanel('intervention')"
                                >
                                    <v-icon left>{{ showInterventionPanel ? 'mdi-close' : 'mdi-plus' }}</v-icon>
                                    {{ showInterventionPanel ? 'Annulla' : 'Intervento' }}
                                </v-btn>
                            </div>
                        </template>
                        <span>Completa prima il triage per poter aggiungere un intervento</span>
                    </v-tooltip>
                    <v-btn
                        color="orange"
                        text
                        @click="togglePanel('private')"
                    >
                        <v-icon left>{{ showPrivateCommentForm ? 'mdi-close' : 'mdi-lock' }}</v-icon>
                        {{ showPrivateCommentForm ? 'Annulla' : 'Nota Privata' }}
                    </v-btn>
                    <v-btn
                        v-if="ticket.status !== 'closed'"
                        color="info"
                        text
                        @click="togglePanel('reply')"
                    >
                        <v-icon left>{{ showReplyForm ? 'mdi-close' : 'mdi-reply' }}</v-icon>
                        {{ showReplyForm ? 'Annulla' : 'Rispondi' }}
                    </v-btn>
                    <v-btn
                        color="purple"
                        text
                        @click="togglePanel('forward')"
                    >
                        <v-icon left>{{ showForwardForm ? 'mdi-close' : 'mdi-share' }}</v-icon>
                        {{ showForwardForm ? 'Annulla' : 'Inoltra' }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="ticket.status !== 'closed'"
                        color="error"
                        text
                        outlined
                        @click="togglePanel('close')"
                    >
                        <v-icon left>{{ showClosePanel ? 'mdi-close' : 'mdi-check-circle' }}</v-icon>
                        {{ showClosePanel ? 'Annulla' : 'Chiudi Ticket' }}
                    </v-btn>
                    <v-btn
                        v-else
                        color="primary"
                        text
                        @click="reopenTicket"
                        :loading="reopeningTicket"
                    >
                        <v-icon left>mdi-refresh</v-icon>
                        Riapri Ticket
                    </v-btn>
                </v-card-actions>

                <!-- Triage Panel -->
                <triage-panel
                    :ticket-id="id"
                    :visible="showTriagePanel"
                    @saved="onTriageSaved"
                    @cancel="showTriagePanel = false"
                ></triage-panel>

                <!-- Form embed: v-show invece di v-if così lo state interno
                     (testo digitato, allegati selezionati) non si perde quando
                     l'utente passa tra Triage / Intervento / Nota Privata / Rispondi / Inoltra / Chiudi. -->

                <!-- Reply Form -->
                <email-reply-form
                    v-show="showReplyForm && ticket.status !== 'closed'"
                    :ticket-id="id"
                    @sent="onReplySent"
                    @cancel="showReplyForm = false"
                ></email-reply-form>

                <!-- Forward Form -->
                <email-forward-form
                    v-show="showForwardForm"
                    :ticket-id="id"
                    @forwarded="onForwarded"
                    @cancel="showForwardForm = false"
                ></email-forward-form>

                <!-- Private Comment Form -->
                <private-comment-form
                    v-show="showPrivateCommentForm"
                    :ticket-id="id"
                    @saved="onCommentSaved"
                    @cancel="showPrivateCommentForm = false"
                ></private-comment-form>

                <!-- Intervention Panel -->
                <intervention-panel
                    v-show="showInterventionPanel"
                    :ticket="ticket"
                    :default-start-date="ticketOpenedDate"
                    :default-start-time="ticketOpenedTime"
                    @saved="onInterventionSaved"
                    @cancel="showInterventionPanel = false"
                ></intervention-panel>

                <!-- Close Ticket Panel -->
                <close-ticket-panel
                    v-show="showClosePanel"
                    :ticket-id="id"
                    @closed="onTicketClosed"
                    @cancel="showClosePanel = false"
                ></close-ticket-panel>
            </v-card>

            <!-- SLA Info Panel -->
            <sla-info-panel
                v-if="sla && sla.sla_name"
                :sla="sla"
            ></sla-info-panel>

            <!-- Timeline Ticket (Maillogs, Comments, Interventions) -->
            <trouble-timeline
                :timeline="timeline"
                :ticket="ticket"
                :loading="loadingTimeline"
                @refresh="loadTimeline"
                @edit-intervention="openEditInterventionDialog"
            ></trouble-timeline>

            <!-- Dialog modifica intervento -->
            <v-dialog v-model="showEditInterventionDialog" max-width="800" persistent>
                <v-card v-if="editingIntervention" class="round-card background-2">
                    <v-card-title class="pa-3">
                        <v-icon color="success" class="mr-3">mdi-wrench</v-icon>
                        <p>Modifica Intervento</p>
                        <v-spacer></v-spacer>
                        <v-btn icon @click="closeEditInterventionDialog">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-4">
                        <fields-generator
                            v-if="editInterventionFieldsset && editInterventionFieldsset.fields"
                            :fieldsset="editInterventionFieldsset"
                            :model="editingIntervention"
                            :force-tinymce="true"
                        ></fields-generator>
                        <div v-else class="text-center grey--text pa-4">
                            Caricamento campi...
                        </div>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="closeEditInterventionDialog">Annulla</v-btn>
                        <v-btn color="success" text @click="saveEditedIntervention" :loading="savingIntervention">
                            <v-icon left>mdi-content-save</v-icon>
                            Salva
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <activities v-if="id"
                class="pb-4"
                model-class="helpdesktrouble"
                model-def="helpdesktroubles"
                :model-id="id"
                :show-comments="false"
            ></activities>

            <!-- Totali Ticket -->
            <v-col class="pa-0 pb-4" v-if="!troubleTotalsHidden">
                <trouble-totals
                    :total="total"
                    :subtotal="subtotal"
                    :tax="tax"
                    :totals-fields-config="troubleTotalsFields"
                    :totals-hidden="troubleTotalsHidden"
                ></trouble-totals>
            </v-col>

            <v-col class="pa-0 pb-4" v-if="id">
                <dms-uploader
                    :id="id"
                    attachable-type="helpdesktrouble"
                ></dms-uploader>
            </v-col>
        </v-col>

        <!-- Colonna Ticket Info (2/12) -->
        <v-col cols="12" lg="2" class="pa-0 pl-lg-4 pb-4">
            <show-generator
                v-if="ticketInfo && ticket.id"
                :info="ticketInfo"
                :model="ticket"
                field-class="col-12"
                :no-buttons="true"
            ></show-generator>

            <!-- Asset Info -->
            <show-generator
                v-if="assetInfo && asset.id"
                :info="assetInfo"
                :model="asset"
                field-class="col-12"
                :no-buttons="true"
                class="mt-4"
            ></show-generator>

            <!-- Contract Info -->
            <show-generator
                v-if="contractInfo && contract.id"
                :info="contractInfo"
                :model="contract"
                field-class="col-12"
                :no-buttons="true"
                class="mt-4"
            ></show-generator>

            <!-- Ordine di vendita connesso (ticket generato dall'ordine) -->
            <show-generator
                v-if="saleorderInfo && saleorder.id"
                :info="saleorderInfo"
                :model="saleorder"
                field-class="col-12"
                :no-buttons="true"
                class="mt-4"
            ></show-generator>
        </v-col>

        <!-- Colonna Company Info (2/12) -->
        <v-col cols="12" lg="2" class="pa-0 pl-lg-4">
            <show-generator
                v-if="companyInfo && company.id"
                :info="companyInfo"
                :model="company"
                field-class="col-12"
                :no-buttons="true"
            ></show-generator>

            <!-- Altri ticket aperti della stessa azienda -->
            <company-open-tickets
                v-if="company.id"
                :company-id="company.id"
                :current-ticket-id="id"
            ></company-open-tickets>

            <!-- Asset dell'azienda -->
            <company-assets
                v-if="company.id"
                :company-id="company.id"
                :current-asset-id="asset.id"
            ></company-assets>

            <!-- Contratti dell'azienda -->
            <company-contracts
                v-if="company.id"
                :company-id="company.id"
                :current-contract-id="contract.id"
            ></company-contracts>

            <!-- Abbonamenti dell'azienda -->
            <company-subscriptions
                v-if="company.id"
                :company-id="company.id"
            ></company-subscriptions>
        </v-col>
    </v-row>
    <v-row v-else-if="loading" class="ma-0">
        <v-col class="text-center pa-8">
            <v-progress-circular
                indeterminate
                color="primary"
            ></v-progress-circular>
        </v-col>
    </v-row>
</template>

<script>
    
import Activities from '@/components/common/Activities'
import ShowGenerator from '@/components/common/ShowGenerator'
import EntityNavigation from '@/components/common/EntityNavigation'
import FieldsGenerator from '@/components/common/FieldsGenerator'
import TriagePanel from '@/components/modules/helpdesk/TriagePanel'
import TicketInlineEdit from '@/components/modules/helpdesk/TicketInlineEdit'
import CompanyAssets from '@/components/modules/helpdesk/CompanyAssets'
import TroubleTotals from '@/components/modules/tickets/TroubleTotals'
import EmailReplyForm from '@/components/modules/helpdesk/EmailReplyForm'
import EmailForwardForm from '@/components/modules/helpdesk/EmailForwardForm'
import ButtonShowGenerator from '@/components/common/ButtonShowGenerator'
import TroubleTimeline from '@/components/modules/helpdesk/TroubleTimeline'
import CloseTicketPanel from '@/components/modules/helpdesk/CloseTicketPanel'
import CompanyContracts from '@/components/modules/helpdesk/CompanyContracts'
import InterventionPanel from '@/components/modules/helpdesk/InterventionPanel'
import PrivateCommentForm from '@/components/modules/helpdesk/PrivateCommentForm'
import CompanyOpenTickets from '@/components/modules/helpdesk/CompanyOpenTickets'
import CompanySubscriptions from '@/components/modules/helpdesk/CompanySubscriptions'
import SlaInfoPanel from '@/components/modules/helpdesk/SlaInfoPanel'

export default {
    name: 'HelpdeskTicket',

    components: {
        Activities,
        EntityNavigation,
        TriagePanel,
        TicketInlineEdit,
        CompanyAssets,
        ShowGenerator,
        FieldsGenerator,
        TroubleTotals,
        EmailReplyForm,
        EmailForwardForm,
        TroubleTimeline,
        CloseTicketPanel,
        CompanyContracts,
        InterventionPanel,
        CompanyOpenTickets,
        PrivateCommentForm,
        ButtonShowGenerator,
        CompanySubscriptions,
        SlaInfoPanel,
    },

    props: {
        id: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            loading: true,
            ticketLoaded: false,
            ticket: {},
            company: {},
            asset: {},
            contract: {},
            saleorder: {},
            timeline: [],
            loadingTimeline: false,
            reopeningTicket: false,
            ticketInfo: null,
            options: {},
            companyInfo: null,
            assetInfo: null,
            contractInfo: null,
            saleorderInfo: null,
            showReplyForm: false,
            showForwardForm: false,
            showPrivateCommentForm: false,
            showTriagePanel: false,
            showInterventionPanel: false,
            showClosePanel: false,
            showEditInterventionDialog: false,
            editingIntervention: null,
            editInterventionFieldsset: null,
            savingIntervention: false,
            ticketOpenedDate: null,
            ticketOpenedTime: null,
            conditionSteps: {
                'triage': 1,
                'service_desk': 2,
                'resolved': 3
            },
            sla: null,
            slaPollingInterval: null,
        }
    },

    computed: {
        currentStep() {
            return this.conditionSteps[this.ticket.condition] || 1
        },

        // Editabilità inline: rispetta i permessi helpdesk (come la pagina di edit).
        // Il backend applica comunque lo stesso permesso dell'edit sul quickUpdate.
        canEditTicket() {
            return this.$store.getters.checkModelPermissions('helpdesktrouble')
        },

        statusOptions() {
            return this.options.statuses || []
        },

        priorityOptions() {
            return this.options.priorities || []
        },

        requestTypeOptions() {
            return this.options.request_types || []
        },

        userOptions() {
            return this.options.users || []
        },

        assignedUserIds() {
            // Gli utenti della show()/quickUpdate arrivano come TroubleUserResource: {value, text, ...}
            return (this.ticket.users || []).map(u => u.value != null ? u.value : u.id)
        },

        total() {
            if (this.ticket?.totals) {
                return (this.ticket.totals.interventions?.total || 0) +
                       (this.ticket.totals.products?.total || 0)
            }
            return this.ticket?.total || 0
        },

        subtotal() {
            if (this.ticket?.totals) {
                return (this.ticket.totals.interventions?.subtotal || 0) +
                       (this.ticket.totals.products?.subtotal || 0)
            }
            return this.ticket?.subtotal || 0
        },

        tax() {
            if (this.ticket?.totals) {
                return (this.ticket.totals.interventions?.tax || 0) +
                       (this.ticket.totals.products?.tax || 0)
            }
            return this.ticket?.tax || 0
        },

        troubleTotalsFields() {
            return this.ticketInfo?.totals_config?.fields || null
        },

        troubleTotalsHidden() {
            return this.ticketInfo?.totals_config?.hidden || false
        },

        blockInterventionsOnTriage() {
            return this.$store.getters.getSettingByModel('helpdesk', 'block_interventions_on_triage') === true
        },

        interventionBlocked() {
            return this.blockInterventionsOnTriage && this.ticket?.condition === 'triage'
        }
    },

    methods: {
        // Apre il panel richiesto chiudendo tutti gli altri.
        // Se il panel era già aperto, lo chiude (toggle classico).
        togglePanel(name) {
            const map = {
                triage: 'showTriagePanel',
                intervention: 'showInterventionPanel',
                private: 'showPrivateCommentForm',
                reply: 'showReplyForm',
                forward: 'showForwardForm',
                close: 'showClosePanel',
            }
            const target = map[name]
            if (!target) return
            const wasOpen = this[target]
            Object.values(map).forEach(key => { this[key] = false })
            this[target] = !wasOpen
        },

        loadTicket() {
            this.loading = true
            this.axios.get(`helpdesktroubles/${this.id}`)
                .then(response => {
                    // Nuova struttura con models e infos
                    this.ticket = response.data.models?.helpdesktrouble || {}
                    this.company = response.data.models?.company || {}
                    this.asset = response.data.models?.asset || {}
                    this.contract = response.data.models?.contract || {}
                    this.saleorder = response.data.models?.saleorder || {}

                    // Opzioni per l'editing inline dei campi principali
                    this.options = response.data.options || {}

                    // Info per ShowGenerator dal backend
                    this.ticketInfo = response.data.infos?.helpdesktrouble || null
                    this.companyInfo = response.data.infos?.company || null
                    this.assetInfo = response.data.infos?.asset || null
                    this.contractInfo = response.data.infos?.contract || null
                    this.saleorderInfo = response.data.infos?.saleorder || null

                    // SLA info
                    this.sla = response.data.models?.sla || null
                    this.startSlaPolling()

                    this.ticketLoaded = true
                    this.loadTimeline()
                })
                .catch(error => {
                    console.error('Error loading ticket:', error)
                    // L'interceptor in main.js gestisce già la snackbar per 403/401/etc
                })
                .finally(() => {
                    this.loading = false
                    this.$store.commit('endProgress')
                })
        },

        loadTimeline() {
            this.loadingTimeline = true
            this.axios.get(`helpdesktroubles/${this.id}/timeline`)
                .then(response => {
                    this.timeline = response.data.data || []
                })
                .catch(error => {
                    console.error('Error loading timeline:', error)
                })
                .finally(() => {
                    this.loadingTimeline = false
                })
        },

        reopenTicket() {
            this.reopeningTicket = true
            this.axios.post(`helpdesktroubles-reopen/${this.id}`)
                .then(response => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Ticket riaperto',
                        color: 'success',
                        timeout: 3000
                    })
                    this.loadTicket()
                })
                .catch(error => {
                    console.error('Error reopening ticket:', error)
                })
                .finally(() => {
                    this.reopeningTicket = false
                })
        },

        onReplySent() {
            this.showReplyForm = false
            this.loadTicket()
            this.$store.commit('setSnackbar', {
                value: true,
                text: 'Risposta inviata',
                color: 'success',
                timeout: 3000
            })
        },

        onForwarded() {
            this.showForwardForm = false
            this.loadTimeline()
            this.$store.commit('setSnackbar', {
                value: true,
                text: 'Email inoltrata',
                color: 'success',
                timeout: 3000
            })
        },

        onCommentSaved() {
            this.showPrivateCommentForm = false
            this.loadTimeline()
            this.$store.commit('setSnackbar', {
                value: true,
                text: 'Nota privata salvata',
                color: 'success',
                timeout: 3000
            })
        },

        onTriageSaved() {
            this.showTriagePanel = false
            this.loadTicket()
        },

        // Aggiornamento locale dopo un edit inline (stato/priorità/tipo/utenti):
        // fonde il model restituito da quickUpdate senza ricaricare tutta la pagina.
        // `users` è sempre presente nella response (anche vuoto), così azzerare gli
        // assegnatari si riflette correttamente nella UI.
        onFieldSaved(data) {
            if (!data) {
                return
            }
            this.ticket = {
                ...this.ticket,
                ...(data.model || {}),
                users: Array.isArray(data.users) ? data.users : (this.ticket.users || [])
            }
        },

        // L'intervento può cambiare lo stato del ticket (e SLA/totali): ricarica
        // tutto il ticket, non solo la timeline (task GMH #7332).
        onInterventionSaved() {
            this.showInterventionPanel = false
            this.loadTicket()
        },

        onTicketClosed() {
            this.showClosePanel = false
            this.loadTicket()
        },

        // Apre il dialog per modificare un intervento
        async openEditInterventionDialog(intervention) {
            try {
                this.$store.commit('startProgress')
                const response = await this.axios.get(`helpdeskinterventions/${intervention.id}/edit`)

                // Imposta i dati dell'intervento
                this.editingIntervention = {
                    ...response.data.model,
                    // Estrai ora da start_date/end_date se non presenti come campi separati
                    start_time: response.data.model.start_time || this.extractTime(response.data.model.start_date),
                    end_time: response.data.model.end_time || this.extractTime(response.data.model.end_date),
                    start_date: this.extractDate(response.data.model.start_date),
                    end_date: this.extractDate(response.data.model.end_date),
                    status: this.ticket.status
                }

                // Ottieni i campi dal backend
                this.editInterventionFieldsset = response.data.fields

                this.showEditInterventionDialog = true
            } catch (error) {
                console.error('Errore caricamento intervento:', error)
                this.$store.commit('setSnackbarError')
            } finally {
                this.$store.commit('endProgress')
            }
        },

        // Chiude il dialog di modifica
        closeEditInterventionDialog() {
            this.showEditInterventionDialog = false
            this.editingIntervention = null
            this.editInterventionFieldsset = null
        },

        // Salva le modifiche all'intervento
        async saveEditedIntervention() {
            if (!this.editingIntervention) return

            this.savingIntervention = true
            await this.axios.put(`helpdeskinterventions/${this.editingIntervention.id}`, {
                ...this.editingIntervention,
                timecardable_id: this.id
            })

            this.$store.commit('setSnackbar', {
                value: true,
                text: 'Intervento aggiornato',
                color: 'success',
                timeout: 3000
            })

            this.closeEditInterventionDialog()
            // Anche la modifica può aggiornare lo stato del ticket
            this.loadTicket()
            this.savingIntervention = false
        },

        // Estrae solo la data da un datetime
        extractDate(datetime) {
            if (!datetime) return null
            return datetime.split(' ')[0] || datetime.split('T')[0]
        },

        startSlaPolling() {
            this.stopSlaPolling()
            if (!this.sla) return
            // Poll every 60 seconds
            this.slaPollingInterval = setInterval(() => {
                this.pollSlaStatus()
            }, 60000)
        },

        stopSlaPolling() {
            if (this.slaPollingInterval) {
                clearInterval(this.slaPollingInterval)
                this.slaPollingInterval = null
            }
        },

        pollSlaStatus() {
            if (!this.sla) return
            this.axios.get(`helpdesktroubles/${this.id}/sla-status`)
                .then(response => {
                    if (response.data.data && this.sla) {
                        this.sla = { ...this.sla, ...response.data.data }
                    }
                })
                .catch(() => {})
        },

        // Estrae solo l'ora da un datetime
        extractTime(datetime) {
            if (!datetime) return null
            const timePart = datetime.split(' ')[1] || datetime.split('T')[1]
            return timePart ? timePart.substring(0, 5) : null
        },
    },

    mounted() {
        const now = new Date()
        this.ticketOpenedDate = this.$format(now, 'yyyy-MM-dd')
        this.ticketOpenedTime = this.$format(now, 'HH:mm')
        this.loadTicket()
    },

    beforeDestroy() {
        this.stopSlaPolling()
    }
}
</script>
